<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import atendimentoAPI from 'dashboard/api/atendimento';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import ConversationLink from '../analytics/ConversationLink.vue';
import { useAnalyticsFormat } from '../analytics/useAnalyticsFormat';
import { teamLabel } from './atendimentoLabels';

// As conversas de uma linha da Agora, de 25 em 25, pedidas só quando a linha é
// aberta — com o recorte da página (o `userId` do filtro incluído) e, na linha de
// um atendente, a pessoa dela em `personId`. Cada uma abre pelo `display_id`, e
// nenhuma leva telefone: o servidor já manda o nome mascarado.
const props = defineProps({
  filters: { type: Object, default: () => ({}) },
});

const { t } = useI18n();
const { number, duration } = useAnalyticsFormat();

const dialogRef = ref(null);
const line = ref('');
const label = ref('');
const person = ref(null);
const page = ref(1);
const result = ref(null);
const loading = ref(false);
const failed = ref(false);
let lastRequest = 0;

const load = async () => {
  lastRequest += 1;
  const request = lastRequest;
  loading.value = true;
  failed.value = false;
  try {
    const { data } = await atendimentoAPI.agoraConversas(
      props.filters,
      line.value,
      { page: page.value, personId: person.value }
    );
    if (request !== lastRequest) return;
    result.value = data;
  } catch {
    if (request !== lastRequest) return;
    result.value = null;
    failed.value = true;
  } finally {
    if (request === lastRequest) loading.value = false;
  }
};

// `personId`: o atendente da linha, ou nada para a linha inteira.
const open = (key, lineLabel, personId = null) => {
  line.value = key;
  label.value = lineLabel;
  person.value = personId;
  page.value = 1;
  result.value = null;
  dialogRef.value?.open();
  load();
};

const goTo = next => {
  page.value = next;
  load();
};

const total = computed(() => result.value?.total || 0);
const pages = computed(() =>
  Math.max(Math.ceil(total.value / (result.value?.per_page || 25)), 1)
);
const conversations = computed(() => result.value?.conversations || []);

// Para quem a conversa vai (quem espera) ou com quem está (com uma pessoa).
const destinationName = destination => {
  if (destination.kind === 'none')
    return t('ATENDIMENTO_REPORTS.NOW.NO_DESTINATION');
  if (destination.kind === 'super_admin')
    return t('ATENDIMENTO_REPORTS.NOW.LIST.SUPER_ADMIN');
  // O time sem nome é o que a conta apagou; a pessoa sem nome saiu da conta.
  if (!destination.name)
    return destination.kind === 'team'
      ? t('ATENDIMENTO_REPORTS.CHANNELS.REMOVED.TEAM')
      : t('ATENDIMENTO_REPORTS.NOW.REMOVED_USER');
  return destination.kind === 'team'
    ? teamLabel(destination.name)
    : destination.name;
};

// Quem espera vai "para" o destino; nas outras linhas, está "com" a pessoa.
const destinationText = destination =>
  line.value === 'waiting'
    ? t('ATENDIMENTO_REPORTS.NOW.LIST.TO', {
        name: destinationName(destination),
      })
    : t('ATENDIMENTO_REPORTS.NOW.LIST.WITH', {
        name: destinationName(destination),
      });

// "WhatsApp · Lia · espera há 12 min · para Suporte".
const detail = item =>
  [
    item.inbox,
    item.agent?.name,
    typeof item.wait_seconds === 'number'
      ? t('ATENDIMENTO_REPORTS.NOW.LIST.WAIT', {
          time: duration(item.wait_seconds),
        })
      : '',
    item.destination ? destinationText(item.destination) : '',
  ]
    .filter(Boolean)
    .join(' · ');

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="2xl"
    overflow-y-auto
    :title="
      t(
        'ATENDIMENTO_REPORTS.PATH.LIST_TITLE',
        { line: label, count: number(total) },
        total
      )
    "
    :show-confirm-button="false"
    :cancel-button-label="t('ATENDIMENTO_REPORTS.PATH.CLOSE')"
  >
    <div
      v-if="loading && !result"
      class="flex gap-2 justify-center items-center py-8 text-sm text-n-slate-11"
    >
      <Spinner :size="16" />
    </div>
    <p v-else-if="failed" class="mb-0 text-sm text-n-ruby-11">
      {{ t('ATENDIMENTO_REPORTS.PATH.LIST_FAILED') }}
    </p>
    <p v-else-if="!conversations.length" class="mb-0 text-sm text-n-slate-11">
      {{ t('ATENDIMENTO_REPORTS.PATH.LIST_EMPTY') }}
    </p>
    <template v-else>
      <ul
        data-test-id="atendimento-agora-lista"
        class="flex flex-col list-none divide-y divide-n-weak"
        :class="{ 'opacity-60': loading }"
      >
        <li
          v-for="item in conversations"
          :key="item.display_id"
          class="flex flex-wrap gap-x-3 gap-y-0.5 justify-between items-baseline py-2 min-w-0 text-sm"
        >
          <span class="flex flex-col min-w-0">
            <span class="truncate text-n-slate-12">
              {{
                item.contact_name || t('ATENDIMENTO_REPORTS.PATH.NO_CONTACT')
              }}
            </span>
            <span class="text-xs break-words text-n-slate-11">
              {{ detail(item) }}
            </span>
            <span
              v-if="item.nobody_online"
              data-test-id="atendimento-agora-lista-nobody-online"
              class="self-start px-1.5 py-0.5 mt-1 text-xs font-medium rounded bg-n-ruby-3 text-n-ruby-11"
            >
              {{ t('ATENDIMENTO_REPORTS.NOW.LIST.NOBODY_ONLINE') }}
            </span>
          </span>
          <ConversationLink :conversation-id="item.display_id" />
        </li>
      </ul>
      <div
        v-if="pages > 1"
        class="flex gap-2 justify-between items-center text-xs text-n-slate-11"
      >
        <Button
          :label="t('ATENDIMENTO_REPORTS.NOW.LIST.PREVIOUS')"
          xs
          slate
          faded
          type="button"
          :disabled="page <= 1 || loading"
          @click="goTo(page - 1)"
        />
        <span>
          {{ t('ATENDIMENTO_REPORTS.PATH.PAGE', { page, pages }) }}
        </span>
        <Button
          :label="t('ATENDIMENTO_REPORTS.NOW.LIST.NEXT')"
          xs
          slate
          faded
          type="button"
          :disabled="page >= pages || loading"
          @click="goTo(page + 1)"
        />
      </div>
    </template>
  </Dialog>
</template>
